// ⚠️ PLAYGROUND-ONLY: This file depends on Firebase and should ONLY be imported by PlaygroundHost.
// Do NOT import from barrel exports (files/index.ts).

import type {
  FilesApi,
  UploadParams,
  UploadResult,
  FileEntry,
  GetUrlParams,
  GetUrlsParams,
  GetUrlsResult,
  GetMetadataParams,
  ExistsParams,
  BatchMetadataParams,
  BatchMetadataResult,
  BatchExistsParams,
  BatchExistsResult,
  ListParams,
  ListResult,
  StorageQuota,
  TransformParams,
  TransformResult,
  ArchiveExtractParams,
  ArchiveExtractResult,
  ConfirmUploadOptions,
  BatchConfirmOptions,
  FilesJobEvent,
  ExportToCloudinaryParams,
  CloudinaryExportResult,
  BatchUploadParams,
  BatchUploadResult,
  BatchConfirmResult,
  BatchCopyParams,
  BatchCopyResult,
} from './FilesApi'
import { getFirebaseClient } from '../firebase/firebaseClient'
import { getCloudRunUrl } from '../config/playground'
import { RundotApiError, RateLimitedError } from '../errors'
import { getPlaygroundGameSessionToken } from '../http/playgroundGameSession'

export class HttpFilesApi implements FilesApi {
  private readonly appId: string

  constructor(appId: string) {
    this.appId = appId
  }

  private async authedFetch(path: string, method: string, body?: unknown): Promise<Response> {
    const client = await getFirebaseClient()
    const idToken = await client.getIdTokenOrThrow()
    const baseUrl = getCloudRunUrl()
    const url = `${baseUrl}/v1/files/${this.appId}/${path}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    }
    const gst = await getPlaygroundGameSessionToken()
    if (gst) headers['x-h5-game-session'] = gst

    const response = await fetch(url, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })

    if (!response.ok) {
      if (response.status === 429) {
        const retryAfterSec = parseInt(response.headers.get('Retry-After') ?? '60', 10)
        throw new RateLimitedError(retryAfterSec * 1000)
      }
      const errorText = await response.text()
      let errorMessage = `Files API error (${response.status})`
      let errorCode = 'UNKNOWN'
      try {
        const errorJson = JSON.parse(errorText) as Record<string, unknown>
        errorMessage = (errorJson.error ?? errorJson.message ?? errorMessage) as string
        if (typeof errorJson.code === 'string') errorCode = errorJson.code
      } catch {
        if (errorText) errorMessage = errorText
      }
      throw new RundotApiError(errorCode, errorMessage, response.status)
    }

    return response
  }

  private async jobsFetch(path: string, method: string, body?: unknown): Promise<Response> {
    const client = await getFirebaseClient()
    const idToken = await client.getIdTokenOrThrow()
    const baseUrl = getCloudRunUrl()
    const url = `${baseUrl}/v1/jobs/${this.appId}/${path}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    }
    const gst = await getPlaygroundGameSessionToken()
    if (gst) headers['x-h5-game-session'] = gst

    const response = await fetch(url, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = `Jobs API error (${response.status})`
      try {
        const errorJson = JSON.parse(errorText)
        errorMessage = errorJson.error || errorJson.message || errorMessage
      } catch {
        if (errorText) errorMessage = errorText
      }
      throw new Error(errorMessage)
    }

    return response
  }

  private async pollJobUntilDone<T>(jobId: string, maxPolls = 120): Promise<T> {
    const POLL_INTERVAL_MS = 1500

    for (let i = 0; i < maxPolls; i++) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS))
      const response = await this.jobsFetch(jobId, 'GET')
      const job = await response.json()
      if (job.status === 'completed') return job.result as T
      if (job.status === 'failed') {
        const err = job.error
        const msg = typeof err === 'string' ? err : err?.message || 'Job failed'
        throw new Error(msg)
      }
    }
    throw new Error('Job polling timed out')
  }

  async upload(params: UploadParams): Promise<UploadResult> {
    const response = await this.authedFetch('upload', 'POST', params)
    return response.json()
  }

  async confirmUpload(key: string, options?: ConfirmUploadOptions): Promise<FileEntry> {
    const response = await this.authedFetch('confirm', 'POST', { key, ...options })
    const data = await response.json()
    if (data.jobId && data.status === 'pending') {
      return this.pollJobUntilDone<FileEntry>(data.jobId)
    }
    return data as FileEntry
  }

  async batchUpload(params: BatchUploadParams): Promise<BatchUploadResult> {
    const response = await this.authedFetch('batch-upload', 'POST', params)
    return response.json()
  }

  async batchCopy(params: BatchCopyParams): Promise<BatchCopyResult> {
    const response = await this.authedFetch('batch-copy', 'POST', params)
    return response.json()
  }

  async batchConfirm(keys: string[], options?: BatchConfirmOptions): Promise<BatchConfirmResult> {
    const response = await this.authedFetch('batch-confirm', 'POST', { keys, ...options })
    return response.json()
  }

  async getUrl(params: GetUrlParams): Promise<string> {
    const response = await this.authedFetch('url', 'POST', params)
    const data = await response.json()
    return data.url
  }

  async getUrls(params: GetUrlsParams): Promise<GetUrlsResult> {
    const response = await this.authedFetch('urls', 'POST', params)
    return response.json()
  }

  async getMetadata(params: GetMetadataParams): Promise<FileEntry> {
    const response = await this.authedFetch('metadata', 'POST', params)
    return response.json()
  }

  async exists(params: ExistsParams): Promise<boolean> {
    const response = await this.authedFetch('exists', 'POST', params)
    const data = await response.json() as { exists: boolean }
    return data.exists
  }

  async batchGetMetadata(params: BatchMetadataParams): Promise<BatchMetadataResult> {
    const response = await this.authedFetch('batch-metadata', 'POST', params)
    return response.json()
  }

  async batchExists(params: BatchExistsParams): Promise<BatchExistsResult> {
    const response = await this.authedFetch('batch-exists', 'POST', params)
    return response.json()
  }

  async delete(key: string): Promise<void> {
    await this.authedFetch('delete', 'POST', { key })
  }

  async list(params?: ListParams): Promise<ListResult> {
    const response = await this.authedFetch('list', 'POST', params ?? {})
    return response.json()
  }

  async getQuota(): Promise<StorageQuota> {
    const response = await this.authedFetch('quota', 'GET')
    return response.json()
  }

  async hasStorageAvailable(sizeBytes: number, contentType?: string): Promise<boolean> {
    const quota = await this.getQuota()
    const cap = contentType === 'application/zip' ? quota.maxArchiveBytes : quota.maxFileBytes
    return quota.availableBytes >= sizeBytes && sizeBytes <= cap
  }

  async setVisibility(key: string, visibility: 'private' | 'public'): Promise<FileEntry> {
    const response = await this.authedFetch('visibility', 'POST', { key, visibility })
    return response.json()
  }

  async transform(params: ArchiveExtractParams): Promise<ArchiveExtractResult>
  async transform(params: TransformParams): Promise<TransformResult>
  async transform(params: TransformParams): Promise<TransformResult | ArchiveExtractResult> {
    const response = await this.authedFetch('transform', 'POST', params)
    const data = await response.json()
    // `unzip` is always an async, multi-output job → poll for the manifest
    // (created[]/skipped[]) rather than a single `entry`.
    if (params.op === 'unzip') {
      if (data.jobId && !data.created) {
        // Archive extraction (download + inflate + fan-out writes) is far heavier
        // than a normal transform; allow ~15 min before giving up on the poll so
        // large gated world archives aren't reported as timed out mid-extraction.
        const UNZIP_MAX_POLLS = 600
        return this.pollJobUntilDone<ArchiveExtractResult>(data.jobId, UNZIP_MAX_POLLS)
      }
      return data as ArchiveExtractResult
    }
    if (data.jobId && !data.entry) {
      const entry = await this.pollJobUntilDone<{ entry: FileEntry }>(data.jobId)
      return { jobId: data.jobId, entry: (entry as unknown as TransformResult).entry ?? entry as unknown as FileEntry }
    }
    return data as TransformResult
  }

  async exportToCloudinary(params: ExportToCloudinaryParams): Promise<CloudinaryExportResult> {
    const response = await this.authedFetch('export-cloudinary', 'POST', params)
    const data = await response.json()
    if (data.jobId && data.status === 'pending') {
      return this.pollJobUntilDone<CloudinaryExportResult>(data.jobId)
    }
    return data as CloudinaryExportResult
  }

  async getCompletedJobs(): Promise<FilesJobEvent[]> {
    const response = await this.jobsFetch('files/completed', 'GET')
    const data = await response.json()
    return data.jobs as FilesJobEvent[]
  }
}
